<template>
  <section class="hero" id="home">
    <div class="container hero__grid">
      <div class="hero__copy">
        <p class="eyebrow">{{ t("hero.eyebrow") }}</p>
        <h1>{{ t("hero.title") }}</h1>
        <p class="lead">{{ t("hero.lead") }}</p>
        <div class="hero__actions">
          <a class="btn btn--primary" :href="quoteHref" target="_blank" rel="noopener">
            {{ t("hero.secondary") }} <span aria-hidden="true">→</span>
          </a>
          <a class="btn btn--ghost" href="#paketler">
            {{ t("packages.eyebrow") }}
          </a>
        </div>
      </div>

      <div class="stage">
        <div class="crm" ref="crmContainerEl" role="region" aria-label="Örnek yazılım paneli" @mouseenter="handleUserInteraction">
          <header class="crm__top">
            <button class="crm__iconbtn" type="button" aria-label="Menü" @click="menuOpen = !menuOpen">
              <svg viewBox="0 0 24 24"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
            </button>
            <button class="crm__plus" type="button" aria-label="Yeni kayıt">+</button>
            <div class="crm__search">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></svg>
              <input v-model="query" type="search" placeholder="Belge veya cari ara" />
            </div>
            <button class="crm__iconbtn" type="button" aria-label="Bildirim">
              <svg viewBox="0 0 24 24"><path d="M6 9a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>
              <i>7</i>
            </button>
            <span class="crm__avatar">SC</span>
          </header>

          <div class="crm__shell">
            <button
              v-if="menuOpen"
              class="crm__dim"
              type="button"
              aria-label="Menüyü kapat"
              @click="menuOpen = false"
            ></button>
            <aside class="crm__side" :class="{ 'is-open': menuOpen }">
              <p>MENÜLER</p>
              <template v-for="item in menu" :key="item.id">
                <template v-if="item.children">
                  <button
                    :id="'crm-nav-' + item.id"
                    type="button"
                    class="crm__nav crm__nav--parent"
                    :class="{ 'is-open': isOpen(item.id), 'is-on': isGroupActive(item) }"
                    @click="toggleGroup(item.id)"
                  >
                    <span>{{ item.name }}</span>
                    <small v-if="item.badge">{{ item.badge }}</small>
                    <svg class="crm__caret" viewBox="0 0 24 24"><path d="m8 10 4 4 4-4" /></svg>
                  </button>
                  <transition name="crm-accordion">
                    <div v-show="isOpen(item.id)" class="crm__sub">
                      <button
                        v-for="child in item.children"
                        :id="'crm-child-' + child.id"
                        :key="child.id"
                        type="button"
                        class="crm__nav crm__nav--child"
                        :class="{ 'is-on': selected === child.id }"
                        @click="pick(child.id)"
                      >
                        <span>{{ child.name }}</span>
                        <small v-if="child.badge">{{ child.badge }}</small>
                      </button>
                    </div>
                  </transition>
                </template>
                <button
                  v-else
                  :id="'crm-nav-' + item.id"
                  type="button"
                  class="crm__nav"
                  :class="{ 'is-on': selected === item.id }"
                  @click="pick(item.id)"
                >
                  <span>{{ item.name }}</span>
                </button>
              </template>
            </aside>

            <div class="crm__main">
              <div class="crm__head">
                <div>
                  <div class="crm__head-title-row">
                    <h2>{{ currentTitle }}</h2>
                    <span class="crm__live-indicator" title="Otomatik CRM Demosu">
                      <span class="crm__live-dot"></span>
                      Canlı Demo
                    </span>
                  </div>
                  <p>{{ currentBreadcrumb }}</p>
                </div>
                <div class="crm__head-actions">
                  <span v-if="selected !== 'home'" class="crm__head-badge">Canlı ERP Verisi</span>
                  <select v-if="selected === 'home'" v-model.number="year">
                    <option :value="2026">Yıl: 2026</option>
                    <option :value="2025">Yıl: 2025</option>
                  </select>
                </div>
              </div>

              <div class="crm__content-body" :class="{ 'is-refreshing': isPageTransitioning }">
                <!-- 1. MODÜL: Yönetici Özeti (Dashboard) -->
                <div v-if="selected === 'home'" class="crm__page crm__page--home">
                  <div class="crm__kpis">
                    <article
                      v-for="(kpi, kIndex) in dashboardKpis"
                      :key="kpi.label"
                      :id="'crm-kpi-' + kIndex"
                      :class="{ 'is-hovered': activeHoverEl === 'crm-kpi-' + kIndex }"
                    >
                      <span>{{ kpi.label }}</span>
                      <strong>{{ kpi.value }}</strong>
                      <em :class="kpi.up ? 'up' : 'down'">{{ kpi.delta }}</em>
                    </article>
                  </div>

                  <div class="crm__grid">
                    <section class="crm__card">
                      <div class="crm__cardh">
                        <div>
                          <h3>Aylık Teklif ve Onaylanan Sipariş</h3>
                          <p>Yıllık performans grafiği</p>
                        </div>
                      </div>
                      <div ref="chartEl" class="crm__chart"></div>
                    </section>

                    <section class="crm__card">
                      <div class="crm__cardh">
                        <div>
                          <h3>Bekleyen Belgeler</h3>
                          <p>{{ filteredDocs.length }} kayıt</p>
                        </div>
                      </div>
                      <div class="crm__tablewrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Belge</th>
                              <th>Cari / Tanım</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(doc, dIndex) in filteredDocs"
                              :key="doc.no"
                              :id="'crm-doc-' + dIndex"
                              :class="{ 'is-hovered': activeHoverEl === 'crm-doc-' + dIndex }"
                            >
                              <td>
                                <b>{{ doc.no }}</b>
                                <small>{{ doc.type }} · {{ doc.amount }}</small>
                              </td>
                              <td>{{ doc.party }}</td>
                              <td>
                                <span class="crm__st" :class="doc.status">{{ doc.statusLabel }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </section>
                  </div>
                </div>

                <!-- 2. MODÜL: Müşteri Kartları & Kredi Limitleri -->
                <div v-else-if="selected === 'customers'" class="crm__page crm__page--customers">
                  <div class="crm__kpis">
                    <article>
                      <span>Kayıtlı Müşteri</span>
                      <strong>{{ customersData.summary.totalCustomers }} Cari</strong>
                      <em class="up">86 Kurumsal</em>
                    </article>
                    <article>
                      <span>Açık Alacak</span>
                      <strong>{{ customersData.summary.totalReceivable }}</strong>
                      <em class="up">32 gün vade</em>
                    </article>
                    <article>
                      <span>Tahsilat Oranı</span>
                      <strong>{{ customersData.summary.collectionScore }}</strong>
                      <em class="up">+%4.2 bu ay</em>
                    </article>
                    <article>
                      <span>Limit Takibi</span>
                      <strong>4 Firma</strong>
                      <em class="down">Kritik seviye</em>
                    </article>
                  </div>

                  <div class="crm__view-panel">
                    <div class="crm__subbar">
                      <div class="crm__pills">
                        <span
                          v-for="(f, fIdx) in customersData.filters"
                          :key="f"
                          class="crm__pill"
                          :class="{ 'is-active': fIdx === 0 }"
                        >{{ f }}</span>
                      </div>
                      <button class="crm__btn-action" type="button">+ Yeni Cari Kart</button>
                    </div>

                    <div class="crm__tablewrap crm__tablewrap--dense">
                      <table>
                        <thead>
                          <tr>
                            <th>Cari Kodu & Ünvan</th>
                            <th>Sektör / Şehir</th>
                            <th>Kredi Limit Kullanımı</th>
                            <th>Skor</th>
                            <th>Yetkili Kişi</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(c, cIndex) in filteredCustomers"
                            :key="c.id"
                            :id="'crm-cust-' + cIndex"
                            :class="{ 'is-hovered': activeHoverEl === 'crm-cust-' + cIndex }"
                          >
                            <td>
                              <b>{{ c.name }}</b>
                              <small class="crm__mono">{{ c.id }}</small>
                            </td>
                            <td>
                              <span class="crm__tag-sector">{{ c.sector }}</span>
                              <small class="crm__city-tag">{{ c.city }}</small>
                            </td>
                            <td class="crm__td-limit">
                              <div class="crm__limit-header">
                                <span>{{ c.balance }}</span>
                                <small>/ {{ c.creditLimit }}</small>
                              </div>
                              <div class="crm__meter">
                                <div
                                  class="crm__meter-fill"
                                  :class="{
                                    'is-safe': c.limitPercent < 60,
                                    'is-warn': c.limitPercent >= 60 && c.limitPercent < 90,
                                    'is-danger': c.limitPercent >= 90
                                  }"
                                  :style="{ width: c.limitPercent + '%' }"
                                ></div>
                              </div>
                            </td>
                            <td>
                              <span class="crm__score-badge" :class="'score-' + c.score.replace('+', 'p').replace('-', 'm')">{{ c.score }}</span>
                            </td>
                            <td>
                              <b>{{ c.contact }}</b>
                              <small>{{ c.phone }}</small>
                            </td>
                            <td>
                              <span class="crm__st" :class="c.limitPercent >= 90 ? 'risk' : (c.limitPercent >= 60 ? 'wait' : 'won')">
                                {{ c.status }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- 3. MODÜL: Satış Fırsatları (Kanban Pipeline) -->
                <div v-else-if="selected === 'opps'" class="crm__page crm__page--opps">
                  <div class="crm__kpis">
                    <article>
                      <span>Pipeline Hacmi</span>
                      <strong>{{ oppsData.summary.pipelineTotal }}</strong>
                      <em class="up">14 Açık Anlaşma</em>
                    </article>
                    <article>
                      <span>Kazanma Oranı</span>
                      <strong>{{ oppsData.summary.avgWinRate }}</strong>
                      <em class="up">+%12 artış</em>
                    </article>
                    <article>
                      <span>Satış Döngüsü</span>
                      <strong>{{ oppsData.summary.cycleDays }}</strong>
                      <em class="up">Hızlı döngü</em>
                    </article>
                    <article>
                      <span>Bu Hafta Kapanış</span>
                      <strong>4 Fırsat</strong>
                      <em class="up">₺1.47M değer</em>
                    </article>
                  </div>

                  <div class="crm__pipeline-board">
                    <div
                      v-for="(col, colIndex) in oppsData.columns"
                      :key="col.id"
                      class="crm__pipeline-col"
                    >
                      <div class="crm__pipeline-colh">
                        <div class="crm__col-title-wrap">
                          <span class="crm__col-dot" :class="'col-dot-' + colIndex"></span>
                          <h4>{{ col.title }}</h4>
                          <span class="crm__col-count">{{ col.count }}</span>
                        </div>
                        <strong>{{ col.total }}</strong>
                      </div>

                      <div class="crm__pipeline-cards">
                        <div
                          v-for="(deal, dIndex) in col.deals"
                          :key="deal.id"
                          :id="'crm-opp-card-' + (colIndex * 2 + dIndex)"
                          class="crm__deal-card"
                          :class="{ 'is-hovered': activeHoverEl === 'crm-opp-card-' + (colIndex * 2 + dIndex) }"
                        >
                          <div class="crm__deal-head">
                            <b>{{ deal.company }}</b>
                            <span class="crm__deal-tag">{{ deal.tag }}</span>
                          </div>
                          <p class="crm__deal-project">{{ deal.project }}</p>
                          <div class="crm__deal-meta">
                            <strong class="crm__deal-amount">{{ deal.amount }}</strong>
                            <span class="crm__deal-prob">{{ deal.probability }}% Olasılık</span>
                          </div>
                          <div class="crm__meter crm__meter--sm">
                            <div class="crm__meter-fill is-safe" :style="{ width: deal.probability + '%' }"></div>
                          </div>
                          <div class="crm__deal-footer">
                            <small>👤 {{ deal.contact }}</small>
                            <small class="crm__deal-days">⏱ {{ deal.daysLeft }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 4. MODÜL: Stok Kartları & Envanter -->
                <div v-else-if="selected === 'items'" class="crm__page crm__page--items">
                  <div class="crm__kpis">
                    <article>
                      <span>Kayıtlı Stok</span>
                      <strong>{{ itemsData.summary.totalSkus }}</strong>
                      <em class="up">12 Ana Kategori</em>
                    </article>
                    <article>
                      <span>Depo Doluluk</span>
                      <strong>{{ itemsData.summary.warehouseCap }}</strong>
                      <em class="up">Merkez Depo A</em>
                    </article>
                    <article>
                      <span>Kritik Seviye</span>
                      <strong>{{ itemsData.summary.criticalItems }} Kalem</strong>
                      <em class="down">Sipariş şart</em>
                    </article>
                    <article>
                      <span>Devir Hızı</span>
                      <strong>{{ itemsData.summary.turnoverRate }}</strong>
                      <em class="up">Hızlı akış</em>
                    </article>
                  </div>

                  <div class="crm__view-panel">
                    <div class="crm__subbar">
                      <div class="crm__location-badge">
                        📍 <span>Depo: <b>{{ itemsData.warehouseName }}</b></span>
                      </div>
                      <button class="crm__btn-action" type="button">+ Yeni Stok Kartı</button>
                    </div>

                    <div class="crm__tablewrap crm__tablewrap--dense">
                      <table>
                        <thead>
                          <tr>
                            <th>SKU / Barkod</th>
                            <th>Ürün Adı & Kategori</th>
                            <th>Lokasyon / Raf</th>
                            <th>Stok Doluluğu</th>
                            <th>Rezerv</th>
                            <th>Maliyet / Satış</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(item, iIndex) in filteredItems"
                            :key="item.sku"
                            :id="'crm-item-' + iIndex"
                            :class="{ 'is-hovered': activeHoverEl === 'crm-item-' + iIndex }"
                          >
                            <td>
                              <b class="crm__mono">{{ item.sku }}</b>
                              <small>{{ item.barcode }}</small>
                            </td>
                            <td>
                              <b>{{ item.name }}</b>
                              <small>{{ item.category }}</small>
                            </td>
                            <td>
                              <span class="crm__shelf-tag">{{ item.location }}</span>
                            </td>
                            <td class="crm__td-limit">
                              <div class="crm__limit-header">
                                <span>{{ item.currentStock }} ad.</span>
                                <small>Min: {{ item.minStock }}</small>
                              </div>
                              <div class="crm__meter">
                                <div
                                  class="crm__meter-fill"
                                  :class="item.stockPercent < 40 ? 'is-danger' : (item.stockPercent < 70 ? 'is-warn' : 'is-safe')"
                                  :style="{ width: item.stockPercent + '%' }"
                                ></div>
                              </div>
                            </td>
                            <td><small>{{ item.reserved }}</small></td>
                            <td>
                              <b>{{ item.salePrice }}</b>
                              <small>Alış: {{ item.unitPrice }}</small>
                            </td>
                            <td>
                              <span class="crm__st" :class="item.stockPercent < 40 ? 'risk' : 'won'">{{ item.status }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- 5. MODÜL: Ürün Reçeteleri (BOM) -->
                <div v-else-if="selected === 'bom'" class="crm__page crm__page--bom">
                  <div class="crm__bom-banner">
                    <div class="crm__bom-info">
                      <div class="crm__bom-badge-row">
                        <span class="crm__bom-code">{{ bomData.product.code }}</span>
                        <span class="crm__bom-ver">{{ bomData.product.version }}</span>
                      </div>
                      <h3>{{ bomData.product.name }}</h3>
                    </div>
                    <div class="crm__bom-cost-card" id="crm-bom-cost">
                      <span>Birim Standart Maliyet</span>
                      <strong>{{ bomData.product.unitCost }}</strong>
                      <small>Montaj: {{ bomData.product.laborTime }} · Onay: {{ bomData.product.approvedBy }}</small>
                    </div>
                  </div>

                  <div class="crm__bom-grid">
                    <div class="crm__bom-components">
                      <div class="crm__cardh">
                        <div>
                          <h3>Reçete Bileşenleri (Hammadde & Yarı Mamul)</h3>
                          <p>4 Ana Bileşen · Fire Payları Dahil</p>
                        </div>
                      </div>
                      <div class="crm__tablewrap crm__tablewrap--dense">
                        <table>
                          <thead>
                            <tr>
                              <th>Bileşen Kodu & Tanım</th>
                              <th>Miktar</th>
                              <th>Fire %</th>
                              <th>Birim Maliyet</th>
                              <th>Toplam</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(comp, cIndex) in bomData.components"
                              :key="comp.code"
                              :id="'crm-bom-comp-' + cIndex"
                              :class="{ 'is-hovered': activeHoverEl === 'crm-bom-comp-' + cIndex }"
                            >
                              <td>
                                <b>{{ comp.name }}</b>
                                <small>{{ comp.code }} · {{ comp.type }}</small>
                              </td>
                              <td><b>{{ comp.qty }}</b></td>
                              <td><small>{{ comp.waste }}</small></td>
                              <td><small>{{ comp.unitCost }}</small></td>
                              <td><strong class="crm__blue-txt">{{ comp.totalCost }}</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div class="crm__bom-ops">
                      <div class="crm__cardh">
                        <div>
                          <h3>Üretim Rotaları</h3>
                          <p>Sıralı Operasyon İstasyonları</p>
                        </div>
                      </div>
                      <div class="crm__ops-timeline">
                        <div v-for="op in bomData.operations" :key="op.step" class="crm__op-step">
                          <span class="crm__op-badge">{{ op.step }}</span>
                          <div class="crm__op-body">
                            <b>{{ op.name }}</b>
                            <small>{{ op.machine }} · ⏱ {{ op.time }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 6. MODÜL: İş Emirleri & Üretim Takip -->
                <div v-else-if="selected === 'jobs'" class="crm__page crm__page--jobs">
                  <div class="crm__kpis">
                    <article>
                      <span>Aktif İş Emri</span>
                      <strong>{{ jobsData.summary.activeWorkOrders }} Hat</strong>
                      <em class="up">4 İstasyon faal</em>
                    </article>
                    <article>
                      <span>Günlük Hedef</span>
                      <strong>{{ jobsData.summary.dailyCompleted }}</strong>
                      <em class="up">Plan dahilinde</em>
                    </article>
                    <article>
                      <span>Kapasite Doluluk</span>
                      <strong>{{ jobsData.summary.capacityLoad }}</strong>
                      <em class="up">Tam vardiya</em>
                    </article>
                    <article>
                      <span>Fire Oranı</span>
                      <strong>{{ jobsData.summary.scrapRate }}</strong>
                      <em class="up">Hedef altında</em>
                    </article>
                  </div>

                  <div class="crm__view-panel">
                    <div class="crm__subbar">
                      <div class="crm__shift-badge">
                        ⚙️ <span>Vardiya: <b>{{ jobsData.shift }}</b></span>
                      </div>
                      <button class="crm__btn-action" type="button">+ Yeni İş Emri Aç</button>
                    </div>

                    <div class="crm__tablewrap crm__tablewrap--dense">
                      <table>
                        <thead>
                          <tr>
                            <th>İş Emri No & Ürün</th>
                            <th>İstasyon / Hat</th>
                            <th>Üretim İlerlemesi</th>
                            <th>Operatör</th>
                            <th>Zaman</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(job, jIndex) in filteredJobs"
                            :key="job.id"
                            :id="'crm-job-' + jIndex"
                            :class="{ 'is-hovered': activeHoverEl === 'crm-job-' + jIndex }"
                          >
                            <td>
                              <b class="crm__mono">{{ job.id }}</b>
                              <small>{{ job.product }}</small>
                            </td>
                            <td>
                              <span class="crm__station-tag">{{ job.line }}</span>
                            </td>
                            <td class="crm__td-limit">
                              <div class="crm__limit-header">
                                <span>{{ job.completedQty }} / {{ job.targetQty }} adet</span>
                                <small>%{{ job.percent }}</small>
                              </div>
                              <div class="crm__meter">
                                <div
                                  class="crm__meter-fill"
                                  :class="job.percent === 100 ? 'is-safe' : (job.percent === 0 ? 'is-danger' : 'is-warn')"
                                  :style="{ width: job.percent + '%' }"
                                ></div>
                              </div>
                            </td>
                            <td><b>{{ job.operator }}</b></td>
                            <td><small>{{ job.duration }}</small></td>
                            <td>
                              <span class="crm__st" :class="job.percent === 100 ? 'won' : (job.percent === 0 ? 'wait' : 'open')">
                                {{ job.status }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- 7. MODÜL: Satınalma Talepleri & Onay Akışı -->
                <div v-else-if="selected === 'pr'" class="crm__page crm__page--pr">
                  <div class="crm__kpis">
                    <article>
                      <span>Bekleyen Talep</span>
                      <strong>{{ purchaseData.summary.pendingApproval }} Talep</strong>
                      <em class="up">Onay sürecinde</em>
                    </article>
                    <article>
                      <span>Onaylanan Bütçe</span>
                      <strong>{{ purchaseData.summary.approvedThisMonth }}</strong>
                      <em class="up">Bu ay onaylı</em>
                    </article>
                    <article>
                      <span>Aktif Tedarikçi</span>
                      <strong>{{ purchaseData.summary.activeSuppliers }} Firma</strong>
                      <em class="up">Anlaşmalı</em>
                    </article>
                    <article>
                      <span>Zamanında Teslim</span>
                      <strong>{{ purchaseData.summary.onTimeDelivery }}</strong>
                      <em class="up">Yüksek başarı</em>
                    </article>
                  </div>

                  <div class="crm__view-panel">
                    <div class="crm__subbar">
                      <div class="crm__filter-badge">
                        📦 <span>Departman Satınalma Talepleri & Onay Hiyerarşisi</span>
                      </div>
                      <button class="crm__btn-action" type="button">+ Yeni Talep Oluştur</button>
                    </div>

                    <div class="crm__tablewrap crm__tablewrap--dense">
                      <table>
                        <thead>
                          <tr>
                            <th>Talep No & Departman</th>
                            <th>Kalemler & Açıklama</th>
                            <th>Tahmini Tutar</th>
                            <th>3 Kademeli Onay Akışı</th>
                            <th>Aciliyet</th>
                            <th>Güncel Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(req, rIndex) in filteredPurchases"
                            :key="req.id"
                            :id="'crm-pr-' + rIndex"
                            :class="{ 'is-hovered': activeHoverEl === 'crm-pr-' + rIndex }"
                          >
                            <td>
                              <b class="crm__mono">{{ req.id }}</b>
                              <small>{{ req.dept }} · {{ req.requester }}</small>
                            </td>
                            <td>
                              <b>{{ req.itemsSummary }}</b>
                              <small>Önerilen: {{ req.preferredSupplier }}</small>
                            </td>
                            <td><strong class="crm__blue-txt">{{ req.estAmount }}</strong></td>
                            <td>
                              <div class="crm__stepper">
                                <span
                                  v-for="step in req.steps"
                                  :key="step.label"
                                  class="crm__step"
                                  :class="'step-' + step.state"
                                >
                                  <small>{{ step.label }}</small>
                                  <i v-if="step.state === 'done'">✓</i>
                                  <i v-else-if="step.state === 'active'">●</i>
                                  <i v-else>○</i>
                                </span>
                              </div>
                            </td>
                            <td>
                              <span class="crm__urgency" :class="'urgency-' + (req.urgency === 'Acil' ? 'high' : (req.urgency === 'Yüksek' ? 'med' : 'low'))">
                                {{ req.urgency }}
                              </span>
                            </td>
                            <td>
                              <span class="crm__st" :class="req.status.includes('Sipariş') ? 'won' : 'open'">
                                {{ req.status }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- 8. MODÜL: Kasa & Banka Yönetimi -->
                <div v-else-if="selected === 'cash'" class="crm__page crm__page--cash">
                  <div class="crm__kpis">
                    <article>
                      <span>Toplam Konsolide Nakit</span>
                      <strong>{{ cashData.summary.totalLiquidity }}</strong>
                      <em class="up">Tüm Hesaplar</em>
                    </article>
                    <article>
                      <span>Günlük Giriş</span>
                      <strong>{{ cashData.summary.dailyInflow }}</strong>
                      <em class="up">Tahsilat & POS</em>
                    </article>
                    <article>
                      <span>Günlük Çıkış</span>
                      <strong>{{ cashData.summary.dailyOutflow }}</strong>
                      <em class="down">Tedarikçi ödemesi</em>
                    </article>
                    <article>
                      <span>Net Günlük Akış</span>
                      <strong>{{ cashData.summary.netCashDelta }}</strong>
                      <em class="up">Pozitif bakiye</em>
                    </article>
                  </div>

                  <div class="crm__acc-grid">
                    <div
                      v-for="(acc, aIndex) in cashData.accounts"
                      :key="acc.id"
                      :id="'crm-cash-acc-' + aIndex"
                      class="crm__acc-card"
                      :class="{ 'is-hovered': activeHoverEl === 'crm-cash-acc-' + aIndex }"
                    >
                      <div class="crm__acc-head">
                        <span class="crm__acc-type">{{ acc.type }}</span>
                        <span class="crm__acc-cur">{{ acc.currency }}</span>
                      </div>
                      <b>{{ acc.name }}</b>
                      <strong class="crm__acc-val">{{ acc.balance }}</strong>
                      <small :class="acc.up ? 'up' : 'down'">Bugün {{ acc.todayChange }}</small>
                    </div>
                  </div>

                  <div class="crm__view-panel" style="margin-top: 6px;">
                    <div class="crm__tablewrap crm__tablewrap--dense">
                      <table>
                        <thead>
                          <tr>
                            <th>Saat / Kod</th>
                            <th>Hesap</th>
                            <th>Cari & Açıklama</th>
                            <th>Tutar</th>
                            <th>Belge No</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(trx, tIndex) in filteredCashTrx"
                            :key="trx.id"
                            :id="'crm-cash-trx-' + tIndex"
                          >
                            <td>
                              <b class="crm__mono">{{ trx.time }}</b>
                              <small class="crm__mono">{{ trx.id }}</small>
                            </td>
                            <td><span class="crm__station-tag">{{ trx.account }}</span></td>
                            <td>
                              <b>{{ trx.party }}</b>
                              <small>{{ trx.desc }}</small>
                            </td>
                            <td>
                              <strong :class="trx.isIncome ? 'crm__txt-income' : 'crm__txt-expense'">
                                {{ trx.amount }}
                              </strong>
                            </td>
                            <td><small class="crm__mono">{{ trx.docNo }}</small></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- 9. MODÜL: e-Fatura / e-Arşiv GİB Portalı -->
                <div v-else-if="selected === 'ebills'" class="crm__page crm__page--ebills">
                  <div class="crm__kpis">
                    <article>
                      <span>GİB Entegrasyonu</span>
                      <strong class="crm__gib-online">{{ ebillsData.summary.gibStatus }}</strong>
                      <em class="up">Web Service Aktif</em>
                    </article>
                    <article>
                      <span>Kalan Kontör</span>
                      <strong>{{ ebillsData.summary.remainingCredits }}</strong>
                      <em class="up">Yeterli bakiye</em>
                    </article>
                    <article>
                      <span>Bu Ay Kesilen</span>
                      <strong>{{ ebillsData.summary.sentThisMonth }}</strong>
                      <em class="up">Hatasız iletim</em>
                    </article>
                    <article>
                      <span>e-Fatura Cirosu</span>
                      <strong>{{ ebillsData.summary.totalTurnover }}</strong>
                      <em class="up">KDV Dahil</em>
                    </article>
                  </div>

                  <div class="crm__view-panel">
                    <div class="crm__subbar">
                      <div class="crm__filter-badge">
                        📜 <span>GİB Onaylı Son e-Fatura ve e-Arşiv Belgeleri</span>
                      </div>
                      <button class="crm__btn-action" type="button">+ Yeni e-Fatura Kes</button>
                    </div>

                    <div class="crm__tablewrap crm__tablewrap--dense">
                      <table>
                        <thead>
                          <tr>
                            <th>Fatura No & ETTN</th>
                            <th>Alıcı Firma & VKN</th>
                            <th>Tip / Senaryo</th>
                            <th>Matrah / KDV</th>
                            <th>Genel Toplam</th>
                            <th>GİB Durumu</th>
                            <th>İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(bill, bIndex) in filteredEbills"
                            :key="bill.id"
                            :id="'crm-ebill-' + bIndex"
                            :class="{ 'is-hovered': activeHoverEl === 'crm-ebill-' + bIndex || activeHoverEl === 'crm-ebill-btn-' + bIndex }"
                          >
                            <td>
                              <b class="crm__mono">{{ bill.id }}</b>
                              <small class="crm__mono crm__ettn">{{ bill.ettn.substring(0, 18) }}...</small>
                            </td>
                            <td>
                              <b>{{ bill.company }}</b>
                              <small>VKN: {{ bill.vkn }}</small>
                            </td>
                            <td>
                              <span class="crm__bill-tag">{{ bill.type }}</span>
                              <small>{{ bill.scenario }}</small>
                            </td>
                            <td>
                              <span>{{ bill.subtotal }}</span>
                              <small>KDV: {{ bill.vat }}</small>
                            </td>
                            <td><strong class="crm__blue-txt">{{ bill.total }}</strong></td>
                            <td>
                              <span class="crm__st" :class="bill.gibCode === 'ok' ? 'won' : 'wait'">
                                {{ bill.gibStatus }}
                              </span>
                            </td>
                            <td>
                              <button :id="'crm-ebill-btn-' + bIndex" class="crm__btn-mini" type="button">Önizle</button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- 10. MODÜL: Servis & SLA Masası -->
                <div v-else-if="selected === 'tickets'" class="crm__page crm__page--tickets">
                  <div class="crm__kpis">
                    <article>
                      <span>Açık Bilet</span>
                      <strong>{{ ticketsData.summary.openTickets }} Talep</strong>
                      <em class="up">Teknik ekip aktif</em>
                    </article>
                    <article>
                      <span>SLA Uyumu</span>
                      <strong>{{ ticketsData.summary.slaCompliance }}</strong>
                      <em class="up">Hedef: %90+</em>
                    </article>
                    <article>
                      <span>İlk Yanıt Süresi</span>
                      <strong>{{ ticketsData.summary.avgFirstResponse }}</strong>
                      <em class="up">Hızlı müdahale</em>
                    </article>
                    <article>
                      <span>Memnuniyet Skoru</span>
                      <strong>{{ ticketsData.summary.satisfactionScore }}</strong>
                      <em class="up">64 Değerlendirme</em>
                    </article>
                  </div>

                  <div class="crm__view-panel">
                    <div class="crm__subbar">
                      <div class="crm__filter-badge">
                        🎧 <span>Müşteri Hizmetleri & Canlı SLA Takip Masası</span>
                      </div>
                      <button class="crm__btn-action" type="button">+ Yeni Destek Bileti</button>
                    </div>

                    <div class="crm__tablewrap crm__tablewrap--dense">
                      <table>
                        <thead>
                          <tr>
                            <th>Bilet No & Müşteri</th>
                            <th>Konu & Başlık</th>
                            <th>Öncelik</th>
                            <th>SLA Kalan Süre</th>
                            <th>Atanan Uzman</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(ticket, tIndex) in filteredTickets"
                            :key="ticket.id"
                            :id="'crm-ticket-' + tIndex"
                            :class="{ 'is-hovered': activeHoverEl === 'crm-ticket-' + tIndex }"
                          >
                            <td>
                              <b class="crm__mono">{{ ticket.id }}</b>
                              <small>{{ ticket.client }}</small>
                            </td>
                            <td><b>{{ ticket.subject }}</b></td>
                            <td>
                              <span class="crm__priority" :class="'priority-' + ticket.priorityCode">
                                {{ ticket.priority }}
                              </span>
                            </td>
                            <td>
                              <span class="crm__sla-tag" :class="{ 'is-safe': ticket.slaSafe }">
                                ⏱ {{ ticket.slaTime }}
                              </span>
                            </td>
                            <td><b>{{ ticket.assignee }}</b></td>
                            <td>
                              <span class="crm__st" :class="ticket.statusCode === 'done' ? 'won' : (ticket.statusCode === 'progress' ? 'open' : 'wait')">
                                {{ ticket.status }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- 11. DİĞER SEÇİMLER İÇİN DİNAMİK FALLBACK GÖRÜNÜMÜ -->
                <div v-else class="crm__page crm__page--fallback">
                  <div class="crm__kpis">
                    <article v-for="(kpi, kIndex) in activeKpis" :key="kpi.label" :id="'crm-kpi-' + kIndex">
                      <span>{{ kpi.label }}</span>
                      <strong>{{ kpi.value }}</strong>
                      <em :class="kpi.up ? 'up' : 'down'">{{ kpi.delta }}</em>
                    </article>
                  </div>
                  <div class="crm__grid">
                    <section class="crm__card">
                      <div class="crm__cardh">
                        <div>
                          <h3>{{ activeChartTitle }}</h3>
                          <p>Aylık analitik dağılım</p>
                        </div>
                      </div>
                      <div ref="chartEl" class="crm__chart"></div>
                    </section>
                    <section class="crm__card">
                      <div class="crm__cardh">
                        <div>
                          <h3>{{ activeTableTitle }}</h3>
                          <p>{{ filteredDocs.length }} kayıt listelendi</p>
                        </div>
                      </div>
                      <div class="crm__tablewrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Belge</th>
                              <th>Cari / Tanım</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="doc in filteredDocs" :key="doc.no">
                              <td><b>{{ doc.no }}</b><small>{{ doc.type }} · {{ doc.amount }}</small></td>
                              <td>{{ doc.party }}</td>
                              <td><span class="crm__st" :class="doc.status">{{ doc.statusLabel }}</span></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Gerçekçi Sanal Mouse İmleci -->
          <div
            v-if="cursorVisible"
            class="crm-cursor"
            :class="{ 'is-clicking': isClicking }"
            :style="{ transform: `translate3d(${cursorPos.x}px, ${cursorPos.y}px, 0)` }"
          >
            <svg class="crm-cursor__svg" viewBox="0 0 24 24">
              <path
                d="M3 3l7 18 2.8-6.8L19.5 11.5 3 3z"
                fill="#0f172a"
                stroke="#ffffff"
                stroke-width="1.5"
                stroke-linejoin="round"
              />
            </svg>
            <span v-if="isClicking" class="crm-cursor__ripple"></span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ApexCharts from "apexcharts";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { whatsappHref } from "@/data/company";
import { crmMenu, dashboardDocs, dashboardKpis } from "@/data/crmDemo";
import {
  crmCustomersData,
  crmOppsData,
  crmItemsData,
  crmBomData,
  crmJobsData,
  crmPurchaseData,
  crmCashData,
  crmEbillsData,
  crmTicketsData,
} from "@/data/crmPages";

const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const seriesByYear = {
  2026: {
    open: [14, 18, 12, 21, 16, 24, 15, 19, 27, 17, 13, 20],
    won: [7, 11, 15, 9, 13, 18, 12, 16, 21, 14, 10, 17],
  },
  2025: {
    open: [11, 13, 10, 16, 14, 18, 12, 15, 19, 14, 11, 16],
    won: [5, 8, 11, 7, 10, 14, 9, 12, 15, 11, 8, 13],
  },
};

const categoryViews = {
  home: {
    title: "Dashboard",
    breadcrumb: "Anasayfa → Dashboard",
    chartTitle: "Aylık teklif / sipariş",
    tableTitle: "Bekleyen belgeler",
    kpis: dashboardKpis,
    docs: dashboardDocs,
  },
  opps: {
    title: "Satış Fırsatları",
    breadcrumb: "Anasayfa → Satış → Satış Fırsatları",
    chartTitle: "Aylık Fırsat & Teklif Dağılımı",
    tableTitle: "Açık Satış Fırsatları",
    kpis: [
      { label: "Açık Fırsat", value: "24", delta: "₺1.2M potansiyel", up: true },
      { label: "Kazanma Oranı", value: "%68", delta: "+%12 bu ay", up: true },
      { label: "Sıcak Takip", value: "7", delta: "48 saatte karar", up: true },
      { label: "Ort. Kapanış", value: "14 gün", delta: "Hızlı döngü", up: true },
    ],
    docs: [
      { no: "F-1092", type: "Fırsat", party: "Borusan Otomotiv", amount: "320.000₺", status: "open", statusLabel: "Görüşmede" },
      { no: "F-1140", type: "Fırsat", party: "Tekno Lojistik A.Ş.", amount: "145.000₺", status: "won", statusLabel: "Kazanıldı" },
      { no: "T-2041", type: "Teklif", party: "Asel Makine Sanayi", amount: "89.000₺", status: "open", statusLabel: "Sunuldu" },
      { no: "F-1188", type: "Fırsat", party: "Yıldız Holding Grup", amount: "450.000₺", status: "wait", statusLabel: "Analizde" },
    ],
  },
  items: {
    title: "Stok Kartları",
    breadcrumb: "Anasayfa → Stok → Stok Kartları",
    chartTitle: "Depo Giriş / Çıkış Hacmi",
    tableTitle: "Kayıtlı Stok Kalemleri",
    kpis: [
      { label: "Kayıtlı Stok", value: "1.420", delta: "12 ana kategori", up: true },
      { label: "Kritik Seviye", value: "6", delta: "Acil sipariş şart", up: false },
      { label: "Depo Doluluk", value: "%84", delta: "Merkez Depo", up: true },
      { label: "Aylık Devir", value: "4.2x", delta: "Hızlı sirkülasyon", up: true },
    ],
    docs: [
      { no: "SK-010", type: "Hammadde", party: "Rulman 6204-2RS", amount: "1.200 ad.", status: "won", statusLabel: "Yeterli" },
      { no: "SK-142", type: "Yarı Mamul", party: "Hidrolik Valf Gövde", amount: "45 ad.", status: "risk", statusLabel: "Kritik Stok" },
      { no: "SK-880", type: "Ticari Mal", party: "Alüminyum Profil 40x40", amount: "380 mt.", status: "open", statusLabel: "Sayımda" },
      { no: "SK-921", type: "Mamul", party: "Endüstriyel Sensör V2", amount: "160 ad.", status: "won", statusLabel: "Hazır" },
    ],
  },
  customers: {
    title: "Müşteri Kartları",
    breadcrumb: "Anasayfa → Cari Hesaplar → Müşteri Kartları",
    chartTitle: "Aylık Müşteri Tahsilat Akışı",
    tableTitle: "Kurumsal Cari Müşteriler",
    kpis: [
      { label: "Aktif Cari", value: "128", delta: "86 kurumsal müşteri", up: true },
      { label: "Açık Bakiye", value: "₺2.4M", delta: "Ort. 32 gün vade", up: true },
      { label: "Vadesi Geçen", value: "4", delta: "Risk limitinde", up: false },
      { label: "Tahsilat Oranı", value: "%94", delta: "+%6 artış", up: true },
    ],
    docs: [
      { no: "C-101", type: "Müşteri", party: "Anadolu Makine Sanayi", amount: "184.000₺", status: "won", statusLabel: "Düzenli" },
      { no: "C-142", type: "Müşteri", party: "Ege Entegre Lojistik", amount: "420.000₺", status: "open", statusLabel: "Vade Bekler" },
      { no: "C-208", type: "Müşteri", party: "Başak Traktör A.Ş.", amount: "95.000₺", status: "won", statusLabel: "Tahsil Edildi" },
      { no: "C-311", type: "Müşteri", party: "Marmara Çelik Yapı", amount: "310.000₺", status: "risk", statusLabel: "Limit Uyarısı" },
    ],
  },
  jobs: {
    title: "İş Emirleri",
    breadcrumb: "Anasayfa → Üretim → İş Emirleri",
    chartTitle: "Atölye Üretim & Kapasite Durumu",
    tableTitle: "Aktif Üretim İş Emirleri",
    kpis: [
      { label: "Aktif İş Emri", value: "9", delta: "4 hatta üretim", up: true },
      { label: "Tamamlanma", value: "%78", delta: "Bugünkü hedef", up: true },
      { label: "Kapasite Yükü", value: "%89", delta: "Tam vardiya", up: true },
      { label: "Fire Oranı", value: "%1.2", delta: "Hedef altında", up: true },
    ],
    docs: [
      { no: "IE-77", type: "Montaj", party: "Güç Ünitesi 24V 500W", amount: "24 adet", status: "open", statusLabel: "Hatta" },
      { no: "IE-82", type: "Talaşlı", party: "Şanzıman Döküm Gövde", amount: "60 adet", status: "won", statusLabel: "Bitti" },
      { no: "IE-90", type: "Kablo", party: "Otomasyon Panosu T4", amount: "8 adet", status: "open", statusLabel: "Testte" },
      { no: "IE-94", type: "Kaynak", party: "Destek Şasisi Ağır Tip", amount: "15 adet", status: "wait", statusLabel: "Sıradaki" },
    ],
  },
  ebills: {
    title: "e-Fatura / e-Arşiv",
    breadcrumb: "Anasayfa → Finans → e-Fatura / e-Arşiv",
    chartTitle: "Haftalık e-Fatura ve Ciro Akışı",
    tableTitle: "Son Düzenlenen e-Faturalar",
    kpis: [
      { label: "Kesilen Fatura", value: "42", delta: "Bu ay e-Fatura", up: true },
      { label: "Toplam Tutar", value: "₺1.8M", delta: "KDV dahil ciro", up: true },
      { label: "GİB Durumu", value: "Onaylı", delta: "Başarılı iletildi", up: true },
      { label: "Kuyruk", value: "0", delta: "Bekleyen yok", up: true },
    ],
    docs: [
      { no: "GIB-091", type: "e-Fatura", party: "Ege Lojistik Ticaret", amount: "210.000₺", status: "won", statusLabel: "Onaylandı" },
      { no: "GIB-092", type: "e-Arşiv", party: "Atlas Kimya Ltd.", amount: "48.500₺", status: "won", statusLabel: "Gönderildi" },
      { no: "GIB-093", type: "e-Fatura", party: "Borusan Enerji", amount: "385.000₺", status: "open", statusLabel: "İşleniyor" },
      { no: "GIB-094", type: "e-Fatura", party: "Örnek Makine A.Ş.", amount: "72.000₺", status: "won", statusLabel: "İletildi" },
    ],
  },
  po: {
    title: "Satınalma Siparişleri",
    breadcrumb: "Anasayfa → Satın Alma → Satınalma Siparişleri",
    chartTitle: "Aylık Satın Alma Harcama Dağılımı",
    tableTitle: "Tedarikçi Satınalma Siparişleri",
    kpis: [
      { label: "Açık Sipariş", value: "12", delta: "Tedarikçi onayında", up: true },
      { label: "Teslim Bekleyen", value: "5", delta: "Bu hafta teslim", up: true },
      { label: "Onaylı Bütçe", value: "₺640B", delta: "%82 kullanıldı", up: true },
      { label: "Zamanında Teslim", value: "%96", delta: "Yüksek performans", up: true },
    ],
    docs: [
      { no: "PO-441", type: "Sipariş", party: "Norm Civata Sanayi", amount: "240 paket", status: "won", statusLabel: "Yolda" },
      { no: "PO-448", type: "Sipariş", party: "Erkunt Döküm A.Ş.", amount: "40 blok", status: "open", statusLabel: "Onayda" },
      { no: "PO-452", type: "Sipariş", party: "Schneider Elektrik", amount: "85 kalem", status: "wait", statusLabel: "Teklifte" },
      { no: "PO-459", type: "Sipariş", party: "Pirelli Hortum", amount: "120 mt.", status: "won", statusLabel: "Teslim" },
    ],
  },
  tickets: {
    title: "Servis Talepleri",
    breadcrumb: "Anasayfa → Destek / Servis → Servis Talepleri",
    chartTitle: "Haftalık Çözülen Destek Çağrıları",
    tableTitle: "Teknik Servis Bildirimleri",
    kpis: [
      { label: "Açık Çağrı", value: "9", delta: "Teknik ekip atandı", up: true },
      { label: "SLA Uyumu", value: "%94", delta: "1. öncelik korundu", up: true },
      { label: "Ort. Çözüm", value: "2.4 sa.", delta: "Hızlı müdahale", up: true },
      { label: "Müşteri Skoru", value: "4.9/5", delta: "64 değerlendirme", up: true },
    ],
    docs: [
      { no: "#1044", type: "Sistem", party: "ERP Lisans Aktivasyon", amount: "Kritik", status: "risk", statusLabel: "SLA Riski" },
      { no: "#1049", type: "Entegrasyon", party: "Sanal POS Zaman Aşımı", amount: "Normal", status: "open", statusLabel: "İnceleniyor" },
      { no: "#1052", type: "Yazılım", party: "Cari Rapor Dışa Aktar", amount: "Düşük", status: "won", statusLabel: "Çözüldü" },
      { no: "#1058", type: "Donanım", party: "Barkod Okuyucu Driver", amount: "Normal", status: "won", statusLabel: "Tamamlandı" },
    ],
  },
  bom: {
    title: "Ürün Reçeteleri (BOM)",
    breadcrumb: "Anasayfa → Üretim → Ürün Reçeteleri",
    chartTitle: "Atölye Üretim & Kapasite Durumu",
    tableTitle: "Aktif Üretim Reçeteleri",
    kpis: [
      { label: "Aktif Reçete", value: "48", delta: "₺1.4M maliyet planı", up: true },
      { label: "Operasyon Sayısı", value: "124", delta: "Standardize edilmiş", up: true },
      { label: "Kapasite Yükü", value: "%89", delta: "Tam vardiya", up: true },
      { label: "Fire Oranı", value: "%1.2", delta: "Hedef altında", up: true },
    ],
    docs: [
      { no: "BOM-01", type: "Reçete", party: "Güç Ünitesi 24V 500W", amount: "24 bileşen", status: "won", statusLabel: "Onaylı" },
      { no: "BOM-04", type: "Reçete", party: "Şanzıman Döküm Gövde", amount: "12 bileşen", status: "won", statusLabel: "Revize" },
      { no: "BOM-08", type: "Reçete", party: "Otomasyon Panosu T4", amount: "45 bileşen", status: "open", statusLabel: "Taslak" },
      { no: "BOM-14", type: "Reçete", party: "Destek Şasisi Ağır Tip", amount: "18 bileşen", status: "won", statusLabel: "Onaylı" },
    ],
  },
  cash: {
    title: "Kasa & Nakit Akışı",
    breadcrumb: "Anasayfa → Finans → Kasa",
    chartTitle: "Haftalık Nakit Akışı ve Tahsilat",
    tableTitle: "Günlük Kasa Hareketleri",
    kpis: [
      { label: "Nakit Bakiye", value: "₺1.84M", delta: "Konsolide nakit", up: true },
      { label: "Günlük Giriş", value: "₺340B", delta: "Tahsilat ve POS", up: true },
      { label: "Günlük Çıkış", value: "₺110B", delta: "Tedarikçi ödemesi", up: true },
      { label: "Net Akış", value: "+₺230B", delta: "Pozitif bakiye", up: true },
    ],
    docs: [
      { no: "KS-01", type: "Merkez TL", party: "Anadolu Makine Tahsilat", amount: "84.000₺", status: "won", statusLabel: "Giriş" },
      { no: "KS-02", type: "USD Kasa", party: "Borusan Enerji Transfer", amount: "$12.500", status: "won", statusLabel: "Giriş" },
      { no: "KS-03", type: "POS Hesabı", party: "E-Ticaret Gün Sonu", amount: "62.400₺", status: "won", statusLabel: "Aktarıldı" },
      { no: "KS-04", type: "Masraf Kasa", party: "Lojistik Akaryakıt", amount: "8.200₺", status: "wait", statusLabel: "Onay Bekler" },
    ],
  },
  pr: {
    title: "Satınalma Talepleri",
    breadcrumb: "Anasayfa → Satın Alma → Satınalma talepleri",
    chartTitle: "Departman Bazlı Harcama Dağılımı",
    tableTitle: "Onay Bekleyen Satınalma Talepleri",
    kpis: [
      { label: "Bekleyen Talep", value: "7", delta: "Onay sürecinde", up: true },
      { label: "Bu Ay Onaylanan", value: "38", delta: "₺640B toplam", up: true },
      { label: "Tedarikçi Sayısı", value: "34", delta: "Aktif anlaşmalı", up: true },
      { label: "Zamanında Teslim", value: "%96", delta: "Yüksek performans", up: true },
    ],
    docs: [
      { no: "TAL-441", type: "Üretim", party: "Norm Civata Bağlantı Elemanı", amount: "240 paket", status: "open", statusLabel: "Onayda" },
      { no: "TAL-448", type: "Döküm", party: "Pik Döküm Gövde Bloğu", amount: "40 adet", status: "wait", statusLabel: "Teklifte" },
      { no: "TAL-452", type: "Elektrik", party: "Schneider Şalt Malzemesi", amount: "85 kalem", status: "won", statusLabel: "Onaylandı" },
      { no: "TAL-459", type: "Bakım", party: "Hidrolik Hortum R2", amount: "120 mt.", status: "won", statusLabel: "Siparişte" },
    ],
  },
};

export default {
  name: "HeroSection",
  setup() {
    const { t } = useI18n();
    const quoteHref = computed(() => whatsappHref(t("contact.waPrefill")));
    const chartEl = ref(null);
    const crmContainerEl = ref(null);
    const query = ref("");
    const selected = ref("home");
    const menuOpen = ref(false);
    const year = ref(2026);
    // Varsayılan olarak tüm menüler tamamen KAPALI başlar!
    const openGroups = ref([]);
    const menu = crmMenu;
    let chart = null;

    const isPageTransitioning = ref(false);

    // Gerçekçi sanal fare imleci durumu
    const cursorPos = ref({ x: 340, y: 150 });
    const cursorVisible = ref(false);
    const isClicking = ref(false);
    const activeHoverEl = ref(null);

    const customersData = crmCustomersData;
    const oppsData = crmOppsData;
    const itemsData = crmItemsData;
    const bomData = crmBomData;
    const jobsData = crmJobsData;
    const purchaseData = crmPurchaseData;
    const cashData = crmCashData;
    const ebillsData = crmEbillsData;
    const ticketsData = crmTicketsData;

    const pageMeta = {
      home: { title: "Yönetici Özeti (Dashboard)", breadcrumb: "Anasayfa → Dashboard" },
      customers: { title: crmCustomersData.title, breadcrumb: crmCustomersData.breadcrumb },
      opps: { title: crmOppsData.title, breadcrumb: crmOppsData.breadcrumb },
      items: { title: crmItemsData.title, breadcrumb: crmItemsData.breadcrumb },
      bom: { title: crmBomData.title, breadcrumb: crmBomData.breadcrumb },
      jobs: { title: crmJobsData.title, breadcrumb: crmJobsData.breadcrumb },
      pr: { title: crmPurchaseData.title, breadcrumb: crmPurchaseData.breadcrumb },
      cash: { title: crmCashData.title, breadcrumb: crmCashData.breadcrumb },
      ebills: { title: crmEbillsData.title, breadcrumb: crmEbillsData.breadcrumb },
      tickets: { title: crmTicketsData.title, breadcrumb: crmTicketsData.breadcrumb },
    };

    const activeView = computed(() => {
      if (categoryViews[selected.value]) {
        return categoryViews[selected.value];
      }
      for (const item of menu) {
        if (item.id === selected.value) {
          return {
            title: item.name,
            breadcrumb: `Anasayfa → ${item.name}`,
            chartTitle: `${item.name} Analizi`,
            tableTitle: `${item.name} Kayıtları`,
            kpis: dashboardKpis,
            docs: dashboardDocs,
          };
        }
        if (item.children) {
          const child = item.children.find((c) => c.id === selected.value);
          if (child) {
            return {
              title: child.name,
              breadcrumb: `Anasayfa → ${item.name} → ${child.name}`,
              chartTitle: `${child.name} Analizi`,
              tableTitle: `${child.name} Kayıtları`,
              kpis: dashboardKpis,
              docs: dashboardDocs,
            };
          }
        }
      }
      return categoryViews.home;
    });

    const currentTitle = computed(() => {
      if (pageMeta[selected.value]) return pageMeta[selected.value].title;
      return activeView.value.title;
    });

    const currentBreadcrumb = computed(() => {
      if (pageMeta[selected.value]) return pageMeta[selected.value].breadcrumb;
      return activeView.value.breadcrumb;
    });

    const activeKpis = computed(() => activeView.value.kpis);
    const activeChartTitle = computed(() => activeView.value.chartTitle);
    const activeTableTitle = computed(() => activeView.value.tableTitle);

    // 10 Farklı Detaylı Modülü Sırayla Gezen Demo Adımları
    const demoSteps = [
      { groupId: "accounts", childId: "customers", targetId: "crm-cust-0" },
      { groupId: "sales", childId: "opps", targetId: "crm-opp-card-0" },
      { groupId: "stock", childId: "items", targetId: "crm-item-0" },
      { groupId: "production", childId: "bom", targetId: "crm-bom-comp-0" },
      { groupId: "production", childId: "jobs", targetId: "crm-job-0" },
      { groupId: "purchase", childId: "pr", targetId: "crm-pr-0" },
      { groupId: "finance", childId: "cash", targetId: "crm-cash-acc-0" },
      { groupId: "finance", childId: "ebills", targetId: "crm-ebill-btn-0" },
      { groupId: "service", childId: "tickets", targetId: "crm-ticket-0" },
      { groupId: "home", childId: "home", targetId: "crm-kpi-0" },
    ];

    let stepIndex = 0;
    let autoTimer = null;
    let resumeTimeout = null;
    let initialTimeout = null;
    let animTimeouts = [];

    function addTimeout(fn, delay) {
      const id = setTimeout(fn, delay);
      animTimeouts.push(id);
      return id;
    }

    function clearAnimTimeouts() {
      animTimeouts.forEach((t) => clearTimeout(t));
      animTimeouts = [];
    }

    function getElPos(id, align = "left") {
      if (typeof document === "undefined" || !crmContainerEl.value) {
        return null;
      }
      const el = document.getElementById(id);
      if (!el) return null;
      const crmRect = crmContainerEl.value.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      if (align === "center") {
        return {
          x: Math.round(elRect.left - crmRect.left + elRect.width / 2),
          y: Math.round(elRect.top - crmRect.top + elRect.height / 2),
        };
      }

      return {
        x: Math.round(elRect.left - crmRect.left + 22),
        y: Math.round(elRect.top - crmRect.top + elRect.height / 2 - 2),
      };
    }

    function isCrmVisible() {
      if (typeof window === "undefined" || !crmContainerEl.value) return false;
      const rect = crmContainerEl.value.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.bottom > 80 && rect.top < vh - 80;
    }

    function scrollChildIntoSide(childEl) {
      if (!childEl || !crmContainerEl.value) return;
      const sideEl = crmContainerEl.value.querySelector(".crm__side");
      if (!sideEl) return;
      const sideRect = sideEl.getBoundingClientRect();
      const childRect = childEl.getBoundingClientRect();

      if (childRect.bottom > sideRect.bottom) {
        sideEl.scrollTop += childRect.bottom - sideRect.bottom + 12;
      } else if (childRect.top < sideRect.top) {
        sideEl.scrollTop -= sideRect.top - childRect.top + 12;
      }
    }

    function triggerPageTransition() {
      isPageTransitioning.value = true;
      setTimeout(() => {
        isPageTransitioning.value = false;
      }, 220);
    }

    function runDemoStep() {
      clearAnimTimeouts();

      if (!isCrmVisible()) {
        cursorVisible.value = false;
        return;
      }

      const step = demoSteps[stepIndex % demoSteps.length];
      stepIndex++;

      const isMobile = typeof window !== "undefined" && window.innerWidth <= 860;
      if (isMobile) {
        menuOpen.value = true;
      }

      const sideEl = crmContainerEl.value?.querySelector(".crm__side");
      if (sideEl && sideEl.scrollTop > 0) {
        sideEl.scrollTop = 0;
      }

      activeHoverEl.value = null;
      cursorVisible.value = true;

      // 1. Durum: Anasayfa (Home)
      if (step.groupId === "home") {
        addTimeout(() => {
          const homePos = getElPos("crm-nav-home", "left");
          if (homePos) cursorPos.value = homePos;

          addTimeout(() => {
            isClicking.value = true;

            addTimeout(() => {
              isClicking.value = false;
              openGroups.value = [];
              triggerPageTransition();
              selected.value = "home";
              if (isMobile) menuOpen.value = false;
              setTimeout(renderChart, 80);

              addTimeout(() => {
                const kpiPos = getElPos("crm-kpi-0", "center");
                if (kpiPos) {
                  cursorPos.value = kpiPos;
                  activeHoverEl.value = "crm-kpi-0";
                }
                addTimeout(() => {
                  isClicking.value = true;
                  addTimeout(() => {
                    isClicking.value = false;
                  }, 150);
                }, 350);

                addTimeout(() => {
                  activeHoverEl.value = null;
                }, 1600);
              }, 360);
            }, 180);
          }, 600);
        }, 50);
        return;
      }

      // 2. Durum: Grup zaten açıksa (örn. Üretim grubundaki BOM -> Jobs geçişi)
      if (openGroups.value.includes(step.groupId)) {
        addTimeout(() => {
          const childEl = document.getElementById(`crm-child-${step.childId}`);
          scrollChildIntoSide(childEl);

          addTimeout(() => {
            const childPos = getElPos(`crm-child-${step.childId}`, "left");
            if (childPos) cursorPos.value = childPos;

            addTimeout(() => {
              isClicking.value = true;

              addTimeout(() => {
                isClicking.value = false;
                triggerPageTransition();
                selected.value = step.childId;
                if (isMobile) menuOpen.value = false;

                addTimeout(() => {
                  const targetPos = getElPos(step.targetId, "center") || getElPos(step.targetId, "left");
                  if (targetPos) {
                    cursorPos.value = targetPos;
                    activeHoverEl.value = step.targetId;
                  }
                  addTimeout(() => {
                    isClicking.value = true;
                    addTimeout(() => {
                      isClicking.value = false;
                    }, 150);
                  }, 350);

                  addTimeout(() => {
                    activeHoverEl.value = null;
                  }, 1600);
                }, 360);
              }, 180);
            }, 550);
          }, 80);
        }, 50);
        return;
      }

      // 3. Durum: Farklı bir ana menü grubu açılacaksa
      addTimeout(() => {
        const parentPos = getElPos(`crm-nav-${step.groupId}`, "left");
        if (parentPos) {
          cursorPos.value = parentPos;
        }

        addTimeout(() => {
          isClicking.value = true;

          addTimeout(() => {
            isClicking.value = false;
            openGroups.value = [step.groupId];

            addTimeout(() => {
              const childEl = document.getElementById(`crm-child-${step.childId}`);
              scrollChildIntoSide(childEl);

              addTimeout(() => {
                const childPos = getElPos(`crm-child-${step.childId}`, "left");
                if (childPos) {
                  cursorPos.value = childPos;
                }

                addTimeout(() => {
                  isClicking.value = true;

                  addTimeout(() => {
                    isClicking.value = false;
                    triggerPageTransition();
                    selected.value = step.childId;
                    if (isMobile) menuOpen.value = false;

                    addTimeout(() => {
                      const targetPos = getElPos(step.targetId, "center") || getElPos(step.targetId, "left");
                      if (targetPos) {
                        cursorPos.value = targetPos;
                        activeHoverEl.value = step.targetId;
                      }

                      addTimeout(() => {
                        isClicking.value = true;
                        addTimeout(() => {
                          isClicking.value = false;
                        }, 150);
                      }, 350);

                      addTimeout(() => {
                        activeHoverEl.value = null;
                      }, 1600);
                    }, 360);
                  }, 180);
                }, 550);
              }, 80);
            }, 260);
          }, 180);
        }, 600);
      }, 50);
    }

    function startAutoDemo() {
      if (autoTimer) clearInterval(autoTimer);
      runDemoStep();
      autoTimer = setInterval(runDemoStep, 5000);
    }

    function handleUserInteraction() {
      cursorVisible.value = false;
      isClicking.value = false;
      activeHoverEl.value = null;
      if (autoTimer) {
        clearInterval(autoTimer);
        autoTimer = null;
      }
      clearAnimTimeouts();

      if (resumeTimeout) clearTimeout(resumeTimeout);
      resumeTimeout = setTimeout(() => {
        startAutoDemo();
      }, 8000);
    }

    const filteredDocs = computed(() => {
      const q = query.value.trim().toLowerCase();
      const docs = activeView.value.docs;
      if (!q) return docs;
      return docs.filter(
        (d) =>
          d.no.toLowerCase().includes(q) ||
          d.type.toLowerCase().includes(q) ||
          d.party.toLowerCase().includes(q)
      );
    });

    const filteredCustomers = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return customersData.customers;
      return customersData.customers.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q) ||
          c.city.toLowerCase().includes(q) ||
          c.sector.toLowerCase().includes(q) ||
          c.contact.toLowerCase().includes(q)
      );
    });

    const filteredItems = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return itemsData.items;
      return itemsData.items.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.sku.toLowerCase().includes(q) ||
          i.barcode.includes(q) ||
          i.location.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q)
      );
    });

    const filteredJobs = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return jobsData.orders;
      return jobsData.orders.filter(
        (j) =>
          j.id.toLowerCase().includes(q) ||
          j.product.toLowerCase().includes(q) ||
          j.line.toLowerCase().includes(q) ||
          j.operator.toLowerCase().includes(q)
      );
    });

    const filteredPurchases = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return purchaseData.requests;
      return purchaseData.requests.filter(
        (r) =>
          r.id.toLowerCase().includes(q) ||
          r.dept.toLowerCase().includes(q) ||
          r.itemsSummary.toLowerCase().includes(q) ||
          r.preferredSupplier.toLowerCase().includes(q)
      );
    });

    const filteredCashTrx = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return cashData.transactions;
      return cashData.transactions.filter(
        (t) =>
          t.party.toLowerCase().includes(q) ||
          t.desc.toLowerCase().includes(q) ||
          t.docNo.toLowerCase().includes(q) ||
          t.account.toLowerCase().includes(q)
      );
    });

    const filteredEbills = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return ebillsData.bills;
      return ebillsData.bills.filter(
        (b) =>
          b.company.toLowerCase().includes(q) ||
          b.id.toLowerCase().includes(q) ||
          b.vkn.includes(q) ||
          b.type.toLowerCase().includes(q)
      );
    });

    const filteredTickets = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return ticketsData.tickets;
      return ticketsData.tickets.filter(
        (t) =>
          t.client.toLowerCase().includes(q) ||
          t.subject.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q) ||
          t.assignee.toLowerCase().includes(q)
      );
    });

    function pick(id) {
      handleUserInteraction();
      triggerPageTransition();
      selected.value = id;
      menuOpen.value = false;
      if (id === "home") {
        setTimeout(renderChart, 80);
      }
    }

    function isOpen(id) {
      return openGroups.value.includes(id);
    }

    function isGroupActive(item) {
      return item.children?.some((child) => child.id === selected.value);
    }

    function toggleGroup(id) {
      handleUserInteraction();
      if (openGroups.value.includes(id)) {
        openGroups.value = openGroups.value.filter((g) => g !== id);
        return;
      }
      openGroups.value = [...openGroups.value, id];
    }

    function chartOptions() {
      const data = seriesByYear[year.value] || seriesByYear[2026];
      const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 600px)").matches;
      const chartMonths = isMobile ? months.slice(6) : months;
      const openData = isMobile ? data.open.slice(6) : data.open;
      const wonData = isMobile ? data.won.slice(6) : data.won;

      return {
        chart: {
          type: "bar",
          stacked: true,
          height: isMobile ? 140 : (typeof window !== "undefined" && window.matchMedia("(max-width: 860px)").matches ? 150 : 168),
          toolbar: { show: false },
          fontFamily: "Plus Jakarta Sans, sans-serif",
          animations: { enabled: false },
        },
        series: [
          { name: "Açık teklif", data: openData },
          { name: "Sipariş", data: wonData },
        ],
        colors: ["#3b82f6", "#16a34a"],
        plotOptions: {
          bar: { columnWidth: isMobile ? "40%" : "46%", borderRadius: 2 },
        },
        dataLabels: { enabled: false },
        grid: { borderColor: "#e8eef4", strokeDashArray: 0, padding: { left: 4, right: 4 } },
        xaxis: {
          categories: chartMonths,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { fontSize: "10px", colors: "#7b8a99" } },
        },
        yaxis: {
          labels: { style: { fontSize: "10px", colors: "#7b8a99" } },
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "11px",
          markers: { width: 8, height: 8, radius: 2 },
        },
        tooltip: {
          y: { formatter: (v) => `${v} belge` },
        },
        stroke: { colors: ["transparent"], width: 2 },
      };
    }

    function destroyChart() {
      if (chart) {
        try {
          chart.destroy();
        } catch (e) {
          // ignore error
        }
        chart = null;
      }
    }

    async function renderChart() {
      if (selected.value !== "home") {
        destroyChart();
        return;
      }
      await nextTick();
      if (!chartEl.value) return;

      destroyChart();

      if (chartEl.value) {
        chartEl.value.innerHTML = "";
      }

      try {
        chart = new ApexCharts(chartEl.value, chartOptions());
        await chart.render();
      } catch (err) {
        console.warn("Chart render error handled:", err);
      }
    }

    function onResize() {
      if (selected.value === "home" && chart) {
        try {
          chart.updateOptions(chartOptions(), false, true);
        } catch (e) {
          renderChart();
        }
      }
      if (window.matchMedia("(min-width: 861px)").matches) {
        menuOpen.value = false;
      }
    }

    onMounted(() => {
      renderChart();
      window.addEventListener("resize", onResize);
      initialTimeout = setTimeout(() => {
        startAutoDemo();
      }, 2000);
    });

    watch(selected, (newVal) => {
      if (newVal === "home") {
        renderChart();
      } else {
        destroyChart();
      }
    });

    watch(year, () => {
      if (selected.value === "home") {
        renderChart();
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", onResize);
      destroyChart();
      if (autoTimer) clearInterval(autoTimer);
      clearAnimTimeouts();
      if (resumeTimeout) clearTimeout(resumeTimeout);
      if (initialTimeout) clearTimeout(initialTimeout);
    });

    return {
      t,
      quoteHref,
      chartEl,
      crmContainerEl,
      query,
      selected,
      menuOpen,
      year,
      menu,
      dashboardKpis,
      filteredDocs,
      filteredCustomers,
      filteredItems,
      filteredJobs,
      filteredPurchases,
      filteredCashTrx,
      filteredEbills,
      filteredTickets,
      customersData,
      oppsData,
      itemsData,
      bomData,
      jobsData,
      purchaseData,
      cashData,
      ebillsData,
      ticketsData,
      currentTitle,
      currentBreadcrumb,
      activeKpis,
      activeChartTitle,
      activeTableTitle,
      isPageTransitioning,
      cursorPos,
      cursorVisible,
      isClicking,
      activeHoverEl,
      isOpen,
      isGroupActive,
      toggleGroup,
      pick,
      handleUserInteraction,
    };
  },
};
</script>

<style scoped>
.crm {
  position: relative;
  width: 100%;
  background: #eef1f5;
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 24px 50px -22px rgba(15, 23, 42, 0.38);
  color: #1e293b;
}

.crm__top {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 10px 0 8px;
  background: #fff;
  border-bottom: 1px solid #e6ebf0;
}

.crm__iconbtn {
  position: relative;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.crm__iconbtn:hover {
  background: #f1f5f9;
}

.crm__iconbtn svg,
.crm__caret,
.crm__search svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: #526274;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex-shrink: 0;
}

.crm__plus {
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
}

.crm__search {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.crm__search input {
  width: 100%;
  border: 0;
  background: transparent;
  outline: none;
  font-size: 12px;
  color: #1e293b;
}

.crm__iconbtn i {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 14px;
  height: 14px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 9px;
  font-style: normal;
  display: flex;
  align-items: center;
  justify-content: center;
}

.crm__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #1e293b;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.crm__shell {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  height: 400px;
}

.crm__side {
  background: #f7f9fb;
  border-right: 1px solid #e6ebf0;
  padding: 8px 6px 12px;
  overflow-y: auto;
}

.crm__side > p {
  margin: 2px 8px 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #8b99a8;
}

.crm__nav {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 6px 8px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5a6a;
  cursor: pointer;
}

.crm__nav span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crm__nav:hover,
.crm__nav.is-open {
  background: #e8eef4;
  color: #0f172a;
}

.crm__nav.is-on {
  background: #dbeafe;
  color: #1d4ed8;
}

.crm__nav small {
  margin-left: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 0 6px;
  font-size: 10px;
  color: #64748b;
}

.crm__caret {
  margin-left: auto;
  transition: transform 0.15s ease;
}

.crm__nav--parent.is-open .crm__caret {
  transform: rotate(180deg);
}

.crm__nav--parent small {
  margin-left: 0;
}

.crm__sub {
  padding: 0 0 4px 10px;
}

/* Akordiyon Menü Yumuşak Açılış Geçişi */
.crm-accordion-enter-active,
.crm-accordion-leave-active {
  transition: max-height 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease;
  max-height: 320px;
  overflow: hidden;
}

.crm-accordion-enter-from,
.crm-accordion-leave-to {
  max-height: 0 !important;
  opacity: 0;
}

.crm__nav--child {
  position: relative;
  padding: 5px 8px;
  font-weight: 500;
  font-size: 11.5px;
  transition: background 0.18s ease, color 0.18s ease, transform 0.15s ease;
}

.crm__main {
  padding: 10px 12px 12px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.crm__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

/* Başlık ve Canlı Demo Rozeti */
.crm__head-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.crm__live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #2563eb;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  letter-spacing: 0.02em;
}

.crm__live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2563eb;
  box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7);
  animation: crmPulseDot 1.8s infinite;
}

@keyframes crmPulseDot {
  0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7); }
  70% { box-shadow: 0 0 0 5px rgba(37, 99, 235, 0); }
  100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
}

/* Gerçekçi Sanal Mouse İmleci */
.crm-cursor {
  position: absolute;
  top: 0;
  left: 0;
  width: 22px;
  height: 22px;
  pointer-events: none;
  z-index: 60;
  transition: transform 0.65s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease;
  will-change: transform;
}

.crm-cursor__svg {
  width: 20px;
  height: 20px;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.38));
  transition: transform 0.12s ease;
  transform-origin: 0 0;
}

.crm-cursor.is-clicking .crm-cursor__svg {
  transform: scale(0.8);
}

.crm-cursor__ripple {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(37, 99, 235, 0.42);
  transform: translate(-50%, -50%) scale(0.2);
  animation: cursorRipple 0.45s ease-out forwards;
}

@keyframes cursorRipple {
  0% {
    opacity: 0.9;
    transform: translate(-50%, -50%) scale(0.2);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(2.2);
  }
}

/* Sayfa Geçişi Canlı Yenilenme Efekti */
.is-refreshing {
  opacity: 0.35;
  filter: blur(1px);
  transform: translateY(2px);
  transition: opacity 0.18s ease, filter 0.18s ease, transform 0.18s ease;
}

.crm__head h2 {
  margin: 0;
  font-size: 15px;
  letter-spacing: -0.03em;
}

.crm__head p {
  margin: 1px 0 0;
  font-size: 11px;
  color: #64748b;
  transition: color 0.2s ease;
}

.crm__head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.crm__head-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #2563eb;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.02em;
}

.crm__head select {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 6px;
  font-size: 11px;
  padding: 5px 8px;
  color: #334155;
}

.crm__content-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.crm__content-body::-webkit-scrollbar,
.crm__tablewrap::-webkit-scrollbar,
.crm__tablewrap--dense::-webkit-scrollbar,
.crm__side::-webkit-scrollbar,
.crm__pipeline-board::-webkit-scrollbar,
.crm__pipeline-cards::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.crm__content-body::-webkit-scrollbar-track,
.crm__tablewrap::-webkit-scrollbar-track,
.crm__tablewrap--dense::-webkit-scrollbar-track,
.crm__side::-webkit-scrollbar-track,
.crm__pipeline-board::-webkit-scrollbar-track,
.crm__pipeline-cards::-webkit-scrollbar-track {
  background: transparent;
}

.crm__content-body::-webkit-scrollbar-thumb,
.crm__tablewrap::-webkit-scrollbar-thumb,
.crm__tablewrap--dense::-webkit-scrollbar-thumb,
.crm__side::-webkit-scrollbar-thumb,
.crm__pipeline-board::-webkit-scrollbar-thumb,
.crm__pipeline-cards::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}

.crm__content-body::-webkit-scrollbar-thumb:hover,
.crm__tablewrap::-webkit-scrollbar-thumb:hover,
.crm__tablewrap--dense::-webkit-scrollbar-thumb:hover,
.crm__side::-webkit-scrollbar-thumb:hover,
.crm__pipeline-board::-webkit-scrollbar-thumb:hover,
.crm__pipeline-cards::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.crm__page {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.crm__view-panel {
  background: #fff;
  border: 1px solid #e6ebf0;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
}

.crm__subbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.crm__pills {
  display: flex;
  gap: 4px;
  overflow-x: auto;
}

.crm__pill {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
}

.crm__pill.is-active {
  background: #dbeafe;
  color: #1e40af;
  font-weight: 700;
}

.crm__btn-action {
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  padding: 4px 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease;
}

.crm__btn-action:hover {
  background: #1d4ed8;
}

.crm__location-badge,
.crm__shift-badge,
.crm__filter-badge {
  font-size: 11px;
  color: #475569;
}

.crm__location-badge b,
.crm__shift-badge b {
  color: #0f172a;
}

.crm__tablewrap--dense {
  overflow: auto;
  flex: 1;
  min-height: 0;
}

.crm__tablewrap--dense table th {
  padding: 4px 6px;
  font-size: 9.5px;
  white-space: nowrap;
}

.crm__tablewrap--dense table td {
  padding: 5px 6px;
  font-size: 10.5px;
  vertical-align: middle;
}

.crm__td-limit {
  min-width: 115px;
}

.crm__limit-header {
  display: flex;
  justify-content: space-between;
  font-size: 9.5px;
  font-weight: 600;
  margin-bottom: 2px;
}

.crm__meter {
  width: 100%;
  height: 5px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.crm__meter--sm {
  height: 4px;
}

.crm__meter-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.crm__meter-fill.is-safe {
  background: #16a34a;
}

.crm__meter-fill.is-warn {
  background: #f59e0b;
}

.crm__meter-fill.is-danger {
  background: #dc2626;
}

.crm__tag-sector {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 700;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 1px 5px;
  color: #334155;
}

.crm__city-tag {
  display: block;
  font-size: 9px;
  color: #64748b;
}

.crm__score-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
}

.crm__score-badge.score-Ap,
.crm__score-badge.score-AAA {
  background: #dcfce7;
  color: #15803d;
}

.crm__score-badge.score-Bp {
  background: #fef3c7;
  color: #b45309;
}

.crm__score-badge.score-Cm {
  background: #fee2e2;
  color: #b91c1c;
}

.crm__shelf-tag,
.crm__station-tag {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 600;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #bfdbfe;
}

.crm__station-tag {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #334155;
}

.crm__mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.crm__ettn {
  color: #64748b;
  font-size: 9px;
}

.crm__blue-txt {
  color: #2563eb;
}

.crm__btn-mini {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 9.5px;
  font-weight: 700;
  padding: 2px 7px;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;
}

.crm__btn-mini:hover {
  background: #e2e8f0;
}

/* Kanban Satış Pipeline */
.crm__pipeline-board {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  flex: 1;
  min-height: 0;
}

.crm__pipeline-col {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
}

.crm__pipeline-colh {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 4px;
  font-size: 10px;
  font-weight: 700;
  color: #334155;
}

.crm__col-title-wrap {
  display: flex;
  align-items: center;
  gap: 5px;
}

.crm__pipeline-colh h4 {
  margin: 0;
  font-size: 10.5px;
  font-weight: 700;
}

.crm__col-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.crm__col-dot.col-dot-0 { background: #3b82f6; }
.crm__col-dot.col-dot-1 { background: #f59e0b; }
.crm__col-dot.col-dot-2 { background: #10b981; }

.crm__col-count {
  background: #e2e8f0;
  border-radius: 999px;
  padding: 0 5px;
  font-size: 9px;
  color: #475569;
}

.crm__pipeline-cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.crm__deal-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.crm__deal-card.is-hovered {
  border-color: #3b82f6;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.18);
  transform: translateY(-2px);
}

.crm__deal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
}

.crm__deal-tag {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  background: #eff6ff;
  color: #1d4ed8;
}

.crm__deal-project {
  font-size: 10px;
  color: #64748b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.crm__deal-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10.5px;
  margin-top: 1px;
}

.crm__deal-amount {
  color: #0f172a;
  font-weight: 800;
}

.crm__deal-prob {
  font-size: 9.5px;
  color: #16a34a;
  font-weight: 700;
}

.crm__deal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 9px;
  color: #64748b;
  margin-top: 2px;
  border-top: 1px solid #f1f5f9;
  padding-top: 3px;
}

/* BOM Reçete Arayüzü */
.crm__bom-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border: 1px solid #e6ebf0;
  border-radius: 8px;
  padding: 8px 12px;
  gap: 8px;
  flex-shrink: 0;
}

.crm__bom-badge-row {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 2px;
}

.crm__bom-code {
  font-family: monospace;
  font-size: 10px;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 1px 6px;
  border-radius: 4px;
}

.crm__bom-ver {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
}

.crm__bom-banner h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
}

.crm__bom-cost-card {
  text-align: right;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 10px;
  transition: all 0.2s ease;
}

.crm__bom-cost-card span {
  display: block;
  font-size: 9.5px;
  color: #64748b;
}

.crm__bom-cost-card strong {
  display: block;
  font-size: 15px;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.crm__bom-cost-card small {
  display: block;
  font-size: 9px;
  color: #64748b;
}

.crm__bom-grid {
  display: grid;
  grid-template-columns: 1.35fr 0.65fr;
  gap: 8px;
  flex: 1;
  min-height: 0;
}

.crm__bom-components,
.crm__bom-ops {
  background: #fff;
  border: 1px solid #e6ebf0;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.crm__ops-timeline {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
  overflow-y: auto;
}

.crm__op-step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.crm__op-badge {
  font-size: 9.5px;
  font-weight: 800;
  background: #2563eb;
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.crm__op-body b {
  display: block;
  font-size: 10.5px;
}

.crm__op-body small {
  color: #64748b;
  font-size: 9.5px;
}

/* PR Onay Stepper */
.crm__stepper {
  display: flex;
  gap: 4px;
  align-items: center;
}

.crm__step {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
}

.crm__step.step-done {
  background: #dcfce7;
  color: #15803d;
}

.crm__step.step-active {
  background: #dbeafe;
  color: #1d4ed8;
  box-shadow: 0 0 0 1px #3b82f6;
}

.crm__step.step-wait {
  background: #f1f5f9;
  color: #94a3b8;
}

.crm__urgency {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
}

.crm__urgency.urgency-high {
  background: #fee2e2;
  color: #b91c1c;
}

.crm__urgency.urgency-med {
  background: #fef3c7;
  color: #b45309;
}

.crm__urgency.urgency-low {
  background: #f1f5f9;
  color: #475569;
}

/* Kasa & Banka Kartları */
.crm__acc-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  flex-shrink: 0;
}

.crm__acc-card {
  background: #fff;
  border: 1px solid #e6ebf0;
  border-radius: 6px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.crm__acc-card.is-hovered {
  border-color: #3b82f6;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.16);
  transform: translateY(-2px);
}

.crm__acc-head {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: #64748b;
}

.crm__acc-cur {
  font-weight: 700;
  background: #f1f5f9;
  padding: 0 4px;
  border-radius: 3px;
}

.crm__acc-card b {
  font-size: 10.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.crm__acc-val {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.crm__txt-income {
  color: #15803d;
  font-weight: 700;
}

.crm__txt-expense {
  color: #b91c1c;
  font-weight: 700;
}

/* e-Fatura & GİB */
.crm__gib-online {
  color: #15803d;
  font-weight: 700;
}

.crm__bill-tag {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 1px 5px;
  border-radius: 4px;
}

/* Servis & SLA Masası */
.crm__priority {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
}

.crm__priority.priority-p1 {
  background: #fee2e2;
  color: #b91c1c;
}

.crm__priority.priority-p2 {
  background: #fef3c7;
  color: #b45309;
}

.crm__priority.priority-p3 {
  background: #eff6ff;
  color: #1d4ed8;
}

.crm__sla-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  background: #fef3c7;
  color: #b45309;
}

.crm__sla-tag.is-safe {
  background: #dcfce7;
  color: #15803d;
}

.crm__kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.crm__kpis article {
  background: #fff;
  border: 1px solid #e6ebf0;
  border-radius: 8px;
  padding: 7px 10px;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.crm__kpis article.is-hovered {
  border-color: #3b82f6;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.16);
  transform: translateY(-2px);
}

.crm__kpis span {
  display: block;
  font-size: 10px;
  color: #64748b;
}

.crm__kpis strong {
  display: block;
  margin: 1px 0;
  font-size: 16px;
  letter-spacing: -0.03em;
}

.crm__kpis em {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
}

.crm__kpis .up {
  color: #15803d;
}

.crm__kpis .down {
  color: #b91c1c;
}

.crm__grid {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 8px;
  min-height: 0;
  flex: 1;
}

.crm__card {
  background: #fff;
  border: 1px solid #e6ebf0;
  border-radius: 8px;
  padding: 8px 10px 6px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.crm__cardh h3 {
  margin: 0;
  font-size: 13px;
}

.crm__cardh p {
  margin: 2px 0 0;
  font-size: 11px;
  color: #64748b;
}

.crm__chart {
  min-height: 168px;
  flex: 1;
}

.crm__tablewrap {
  overflow: auto;
  margin-top: 4px;
  flex: 1;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

th {
  text-align: left;
  font-size: 10px;
  color: #7b8a99;
  font-weight: 700;
  padding: 5px 8px;
  border-bottom: 1px solid #eef2f6;
}

td {
  padding: 6px 8px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  white-space: nowrap;
}

tr:hover td,
tr.is-hovered td {
  background: #eff6ff;
  transition: background 0.18s ease;
}

td b {
  display: block;
  font-size: 12px;
}

td small {
  color: #64748b;
}

.crm__st {
  display: inline-flex;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
}

.crm__st.open {
  background: #dbeafe;
  color: #1d4ed8;
}

.crm__st.wait {
  background: #fef3c7;
  color: #b45309;
}

.crm__st.won {
  background: #dcfce7;
  color: #15803d;
}

.crm__st.risk {
  background: #fee2e2;
  color: #b91c1c;
}

.crm__dim {
  display: none;
}

@media (max-width: 1180px) {
  .crm__kpis {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 860px) {
  .crm {
    border-radius: 14px;
  }

  .crm__shell {
    position: relative;
    grid-template-columns: 1fr;
    height: auto;
  }

  .crm__dim {
    display: block;
    position: absolute;
    inset: 0;
    z-index: 4;
    border: 0;
    background: rgba(15, 23, 42, 0.38);
  }

  .crm__side {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: min(82%, 280px);
    z-index: 5;
    transform: translateX(-105%);
    transition: transform 0.2s ease;
    box-shadow: 16px 0 40px rgba(15, 23, 42, 0.18);
  }

  .crm__side.is-open {
    transform: none;
  }

  .crm__search input {
    font-size: 16px; /* Prevents iOS Safari auto-zoom */
  }

  .crm__head select {
    font-size: 13px;
  }

  .crm__main {
    min-height: auto;
    padding: 10px 10px 14px;
  }

  .crm__grid {
    grid-template-columns: 1fr;
  }

  .crm__bom-grid {
    grid-template-columns: 1fr;
  }

  .crm__bom-banner {
    flex-direction: column;
    align-items: flex-start;
  }

  .crm__bom-cost-card {
    text-align: left;
    width: 100%;
    margin-top: 4px;
  }

  .crm__pipeline-board {
    grid-template-columns: 240px 240px 240px;
    overflow-x: auto;
    padding-bottom: 6px;
  }

  .crm__acc-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .crm__chart {
    min-height: 140px;
  }

  .crm__tablewrap {
    max-height: 200px;
  }

  .crm__plus {
    display: none;
  }

  .crm__nav,
  .crm__nav--child {
    min-height: 40px;
  }
}

@media (max-width: 540px) {
  .crm__kpis {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }

  .crm__kpis article {
    padding: 8px 10px;
  }

  .crm__kpis strong {
    font-size: 16px;
  }

  .crm__kpis span {
    font-size: 10px;
  }

  .crm__acc-grid {
    grid-template-columns: 1fr;
  }

  .crm__subbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}

@media (max-width: 400px) {
  th:nth-child(2),
  td:nth-child(2) {
    display: none;
  }
}
</style>
